import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { getEmployeeSuggestions, Employee } from '../dataProvider';

export interface EmployeePickerProps {
    label: string;
    value: Employee | null;
    onChange: (value: Employee | null) => void;
    disabled?: boolean;
}

export const EmployeePicker: React.FC<EmployeePickerProps> = ({
    label,
    value,
    onChange,
    disabled = false
}) => {
    const [options, setOptions] = useState<Employee[]>([]);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');

    useEffect(() => {
        if (inputValue.length < 2) {
            setOptions([]);
            return;
        }

        const fetchSuggestions = async () => {
            setLoading(true);
            try {
                const data = await getEmployeeSuggestions(inputValue);
                setOptions(data);
            } catch (error) {
                console.error('Error fetching employee suggestions:', error);
                setOptions([]);
            } finally {
                setLoading(false);
            }
        };

        const debounceTimer = setTimeout(fetchSuggestions, 300);
        return () => clearTimeout(debounceTimer);
    }, [inputValue]);

    return (
        <Autocomplete
            options={options}
            loading={loading}
            disabled={disabled}
            value={value}
            onChange={(_, newValue) => {
                onChange(newValue);
            }}
            inputValue={inputValue}
            onInputChange={(_, newInputValue) => {
                setInputValue(newInputValue);
            }}
            getOptionLabel={(option) => `${option.full_name} (${option.email})`}
            isOptionEqualToValue={(option, val) => option.employee_id === val.employee_id}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder="Start typing to search..."
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};
